import React, { useContext, useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import { CheckCircleOutline, Close } from "@mui/icons-material";

import { AuthContext } from "@/auth/AuthContext";
import { customFirestore } from "@/firebase";
import apiClient from "@/utils/api-client";
import { type CodeReviewFinding, type ProposedPr } from "@/utils/types";

const SOURCE_LABEL: Record<ProposedPr["source"], string> = {
  upload: "Build upload",
  failure: "Test failure",
  pr_mention: "PR mention",
};

/**
 * Superuser-only review queue for agent-prepared fixes. Each proposed PR shows
 * the findings + the exact diff; "Create PR" publishes it (backend re-clones the
 * pinned commit, re-applies the diff, pushes, and opens the PR).
 */
const ProposedPRsPage: React.FC = () => {
  const { user } = useContext<any>(AuthContext);
  const isSuperUser = user?.role === "superUser";
  const orgId: string | undefined = user?.companyId;

  const [proposals, setProposals] = useState<ProposedPr[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Just-created proposals: the Firestore listener drops them from the pending
  // list, so we keep a compact "created" card locally with a link to the PR.
  const [created, setCreated] = useState<
    { id: string; title: string; repo: string; url: string }[]
  >([]);

  useEffect(() => {
    if (!isSuperUser || !orgId) {
      setLoading(false);
      return;
    }
    const q = query(
      collection(customFirestore, "proposedPrs"),
      where("organisationId", "==", orgId),
      where("status", "==", "pending"),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setProposals(
          snap.docs.map((d) => ({ id: d.id, ...(d.data() as object) }) as ProposedPr),
        );
        setLoading(false);
      },
      (e) => {
        setError(e.message);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [isSuperUser, orgId]);

  const act = async (id: string, action: "create" | "reject") => {
    setBusyId(id);
    setError(null);
    try {
      const res = await apiClient.post(`/github_app/proposed_prs/${id}/${action}`);
      // The Firestore listener will drop it from the pending list on success.
      if (action === "create" && res?.data?.prUrl) {
        const p = proposals.find((x) => x.id === id);
        setCreated((prev) => [
          { id, title: p?.title ?? "Proposed fix", repo: p?.repo ?? "", url: res.data.prUrl },
          ...prev.filter((x) => x.id !== id),
        ]);
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Request failed");
    } finally {
      setBusyId(null);
    }
  };

  if (!isSuperUser) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning">This page is restricted to superusers.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%", overflowY: "auto", width: "100%" }}>
      <Box sx={{ p: 3, maxWidth: 1100, mx: "auto" }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          Proposed PRs
        </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {created.map((c) => (
        <CreatedCard
          key={c.id}
          item={c}
          onDismiss={() => setCreated((prev) => prev.filter((x) => x.id !== c.id))}
        />
      ))}
      {loading ? (
        <CircularProgress />
      ) : proposals.length === 0 ? (
        created.length === 0 && (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            No proposed PRs awaiting review.
          </Typography>
        )
      ) : (
        proposals.map((p) => (
          <ProposalCard
            key={p.id}
            proposal={p}
            busy={busyId === p.id}
            onCreate={() => act(p.id, "create")}
            onReject={() => act(p.id, "reject")}
          />
        ))
      )}
      </Box>
    </Box>
  );
};

const CreatedCard: React.FC<{
  item: { id: string; title: string; repo: string; url: string };
  onDismiss: () => void;
}> = ({ item, onDismiss }) => (
  <Paper
    elevation={0}
    sx={{
      p: 1.25,
      mb: 1.5,
      borderRadius: 3,
      background: "rgba(46,125,50,0.12)",
      border: "1px solid rgba(46,125,50,0.35)",
    }}
  >
    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
      <CheckCircleOutline sx={{ color: "#2e7d32" }} fontSize="small" />
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        PR created
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {item.title}
      </Typography>
      {item.repo && (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {item.repo}
        </Typography>
      )}
      <Box flexGrow={1} />
      <Button
        size="small"
        variant="outlined"
        color="success"
        href={item.url}
        target="_blank"
        rel="noopener noreferrer"
      >
        View PR
      </Button>
      <IconButton size="small" onClick={onDismiss} aria-label="dismiss">
        <Close fontSize="small" />
      </IconButton>
    </Box>
  </Paper>
);

const ProposalCard: React.FC<{
  proposal: ProposedPr;
  busy: boolean;
  onCreate: () => void;
  onReject: () => void;
}> = ({ proposal, busy, onCreate, onReject }) => {
  const repoLabel =
    proposal.repo + (proposal.commitSha ? `@${proposal.commitSha.slice(0, 7)}` : "");
  return (
    <Paper elevation={2} sx={{ p: 2, mb: 2, borderRadius: 3 }}>
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {proposal.title}
        </Typography>
        <Chip size="small" label={SOURCE_LABEL[proposal.source] ?? proposal.source} />
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {repoLabel}
          {proposal.baseBranch ? ` → ${proposal.baseBranch}` : ""}
        </Typography>
        <Box flexGrow={1} />
        <Button variant="contained" size="small" disabled={busy} onClick={onCreate}>
          {busy ? "Working…" : "Create PR"}
        </Button>
        <Button variant="outlined" color="inherit" size="small" disabled={busy} onClick={onReject}>
          Reject
        </Button>
      </Box>

      {(proposal.findings ?? []).map((f, i) => (
        <FindingRow key={i} finding={f} />
      ))}

      <Divider sx={{ my: 1.5 }} />
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        Proposed change
      </Typography>
      <DiffView diff={proposal.diff} />
    </Paper>
  );
};

const FindingRow: React.FC<{ finding: CodeReviewFinding }> = ({ finding }) => {
  const loc = finding.filePath + (finding.lineStart ? `:${finding.lineStart}` : "");
  return (
    <Box sx={{ mb: 1 }}>
      <Box display="flex" alignItems="center" gap={1}>
        <Chip size="small" label={finding.severity} />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {finding.title}
        </Typography>
        <Typography variant="caption" sx={{ fontFamily: "monospace", color: "text.secondary" }}>
          {loc}
        </Typography>
      </Box>
      {finding.suggestedImprovement && (
        <Typography variant="body2" sx={{ color: "text.secondary", ml: 0.5 }}>
          {finding.suggestedImprovement}
        </Typography>
      )}
    </Box>
  );
};

const DiffView: React.FC<{ diff: string }> = ({ diff }) => {
  const lines = useMemo(() => (diff || "").split("\n"), [diff]);
  const colorFor = (line: string): string | undefined => {
    if (line.startsWith("+") && !line.startsWith("+++")) return "#1b5e20";
    if (line.startsWith("-") && !line.startsWith("---")) return "#b71c1c";
    if (line.startsWith("@@")) return "#0277bd";
    if (line.startsWith("diff ") || line.startsWith("index ")) return "#6a1b9a";
    return undefined;
  };
  return (
    <Box
      component="pre"
      sx={{
        mt: 0.5,
        p: 1.5,
        borderRadius: 1,
        background: "rgba(0,0,0,0.04)",
        overflowX: "auto",
        fontSize: "0.8rem",
        lineHeight: 1.4,
        fontFamily: "monospace",
        whiteSpace: "pre",
      }}
    >
      {lines.map((line, i) => (
        <div key={i} style={{ color: colorFor(line) }}>
          {line || " "}
        </div>
      ))}
    </Box>
  );
};

export default ProposedPRsPage;
